/* eslint-disable @next/next/no-img-element */
"use client"
import { useAccount } from '@/lib/hooks/store/useAccount'
import { useAPI, useQueryParams } from '@aw/lib'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { useCallback, useEffect, useState } from 'react'
import LoginForm from './form'
import TwoFAChallenge from './two-factor'


const Login = () => {
    const api = useAPI()
    const query = useQueryParams()
    const { user, fetchUser } = useAccount()

    const redirectionHandler = useCallback(({ type }: { type: string }) => {
        const redirect = query.get('redirect')
        if (redirect && (redirect.startsWith('http://') || redirect.startsWith('https://'))) {
            window.location.href = redirect
            return
        }
        switch (type) {
            case 'APPLICANT':
                window.location.href = `${process.env.NEXT_PUBLIC_USER_URL}/${redirect || 'dashboard'}`
                break
            case 'ORG_INTERNAL_MEMBER':
                window.location.href = `${process.env.NEXT_PUBLIC_ORG_URL}/${redirect || 'welcome'}`
                break
            case 'ORG_CLIENT':
                window.location.href = `${process.env.NEXT_PUBLIC_CLIENT_URL}/${redirect || 'welcome'}`
        }
    }, [])

    const [showTwoFAForm, setShowTwoFAForm] = useState(false)



    // Check if user is already logged in and redirect
    useEffect(() => {
        if (user?.type) {
            redirectionHandler({ type: user.type })
        }
    }, [user, redirectionHandler])

    const loginRequest = api.post('/login', { service: "auth" })
    const twoFARequest = api.post('/two-factor-challenge', { service: "auth" })

    const submitLogin = (data: unknown) => loginRequest.mutate(data, {
        onSuccess(data) {
            if (data?.two_factor) {
                setShowTwoFAForm(true)
            } else if (data?.type) {
                fetchUser().then(() => {
                    redirectionHandler({ type: data.type })
                })
            }
        },
        onError(error) {
            console.error('Login failed:', error)
        }
    })

    const submitPin = useCallback((data: { [x: string]: string; }) => twoFARequest.mutate(data, {
        onSuccess(data) {
            if (data?.type) {
                // Refresh user data in Redux after successful 2FA
                fetchUser().then(() => {
                    redirectionHandler({ type: data.type })
                })
            }
        }
    }), [twoFARequest, redirectionHandler, fetchUser])

    return <> <div className="h-screen w-full flex items-start">
        <div className="w-0 xl:w-1/2 h-full flex items-center justify-center"
            style={{
                background: "linear-gradient(24deg, #29E6BF -1.73%, #7F5FFF 93.44%)",
            }}
        >
            <div className="w-[50rem]">

                <div className="text-gunmetal-lightest font-montserrat text-[1.5rem] font-normal">
                    Find. Onboard. Manage.
                </div>

                <div className="text-[3.5rem] font-semibold leading-[4rem] text-gunmetal-lightest font-montserrat">
                    Full service global employment solution
                </div>
                <div className="text-gunmetal-lightest font-montserrat text-[1.25rem] leading-7">
                    Harness global talent with our all-in-one solution! Easily find and assess international workers, monitor productivity, and manage payroll seamlessly.
                </div>
                <div className="mt-16">
                    <img src={"/vectors/login-vector.svg"} alt="Login Illustration" />
                </div>
            </div>
        </div>
        <div className="w-full p-4 xl:p-0 xl:w-1/2 h-full flex items-center justify-center overflow-y-auto bg-gunmetal-50 dark:bg-gunmetal-50-dark">
            <div className="w-full max-w-120 py-8">
                {showTwoFAForm ? (
                    <TwoFAChallenge submit={submitPin} twoFARequest={twoFARequest} formErrors={twoFARequest?.error?.response?.data} />
                ) : (
                    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENTID!}>
                        <LoginForm
                            loginRequest={loginRequest}
                            submit={submitLogin}
                            onGoogleLoginSuccess={(data: { type: string }) => {
                                fetchUser().then(() => {
                                    redirectionHandler({ type: data.type })
                                })
                            }}
                            formErrors={loginRequest?.error?.response?.data}
                        />
                    </GoogleOAuthProvider>
                )}
            </div>
        </div>
    </div>
    </>

}

export default Login
